/**
 * Pure ORDER 20A CVD intensity and marker model.
 *
 * The denominator is session-relative: max(running RTH-session max |delta|, previous full RTH
 * session p90 |delta|). A previous-session floor is admitted only when the caller proves that
 * the entire RTH session is present; a clipped history must remain visibly floor-unavailable.
 */

export type CvdBucket = 0 | 1 | 2 | 3;

export interface CvdIntensityInput {
  t: number;
  delta: number;
  /** 23-hour ES trading-session key. */
  session: string;
  /** New York RTH date, or null outside 09:30–16:00 ET. */
  rthSession: string | null;
}

export interface CvdIntensityBar extends CvdIntensityInput {
  denom: number;
  pct: number;
  bucket: CvdBucket;
  previousFloorAvailable: boolean;
}

export interface CvdPriceRange {
  t: number;
  h: number;
  l: number;
  session: string;
  rthSession?: string | null;
}

export interface CvdHypothesisMarker {
  kind: "ABS" | "EXH";
  t: number;
  sourceT: number;
  label: "ABS?" | "EXH?";
}

export interface CvdDivergenceInput {
  t: number;
  cvd: number;
  priceHigh: number;
  priceLow: number;
  delta: number;
}

export interface ExhaustionLowInput {
  t: number;
  delta: number;
  priceHigh: number;
  priceLow: number;
  close: number;
  session: string;
}

export interface ExhaustionLowMark {
  t: number;
  pushContracts: number;
  pushContractsPerTick: number;
  legContractsPerTick: number;
  ticks: number;
}

export interface CvdDivergencePair {
  id: number;
  kind: "higher-cvd-high" | "lower-cvd-low";
  previousSwingAt: number;
  swingAt: number;
  confirmedAt: number;
  w: number;
  contracts: number;
  previousCvd: number;
  currentCvd: number;
  previousPrice: number;
  currentPrice: number;
  priceTicks: number;
}

const finiteAbs = (value: number) => Number.isFinite(value) ? Math.abs(value) : 0;

const rthClock = new Intl.DateTimeFormat("en-CA", {
  timeZone: "America/New_York",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  hourCycle: "h23",
});

export interface RthSessionPoint {
  key: string;
  minute: number;
}

/** Point-in-time RTH membership, including DST through the IANA New York clock. */
export function rthSessionAt(t: number): RthSessionPoint | null {
  if (!Number.isFinite(t)) return null;
  const parts = Object.fromEntries(rthClock.formatToParts(new Date(t))
    .filter((part) => part.type !== "literal")
    .map((part) => [part.type, part.value]));
  const minute = Number(parts.hour) * 60 + Number(parts.minute);
  if (minute < 9 * 60 + 30 || minute >= 16 * 60) return null;
  return { key: `${parts.year}-${parts.month}-${parts.day}`, minute };
}

/** Nearest-rank linear percentile over a finite, sorted copy. */
export function percentile(values: readonly number[], p: number): number | null {
  const sorted = values.filter(Number.isFinite).slice().sort((a, b) => a - b);
  if (!sorted.length) return null;
  const at = Math.max(0, Math.min(1, p)) * (sorted.length - 1);
  const lo = Math.floor(at);
  const hi = Math.ceil(at);
  if (lo === hi) return sorted[lo];
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (at - lo);
}

export function cvdBucket(delta: number, denom: number): CvdBucket {
  if (!Number.isFinite(denom) || denom <= 0) return 0;
  const pct = finiteAbs(delta) / denom;
  if (pct < .25) return 0;
  if (pct < .5) return 1;
  if (pct < .75) return 2;
  return 3;
}

/** Build contiguous session groups without pooling or sorting across their knowledge-time order. */
export function buildCvdIntensity(
  bars: readonly CvdIntensityInput[],
  previousFullSessionP90: number | null = null,
  completeSessions: ReadonlySet<string> = new Set(),
): CvdIntensityBar[] {
  const output: CvdIntensityBar[] = [];
  let cursor = 0;
  let previousFloor = previousFullSessionP90;
  while (cursor < bars.length) {
    const session = bars[cursor].session;
    let end = cursor + 1;
    while (end < bars.length && bars[end].session === session) end += 1;
    const group = bars.slice(cursor, end);
    const magnitudes = group.map((bar) => finiteAbs(bar.delta));
    let runningMax = 0;
    for (const bar of group) {
      runningMax = Math.max(runningMax, finiteAbs(bar.delta));
      const denom = Math.max(runningMax, previousFloor ?? 0);
      output.push({
        ...bar,
        denom,
        pct: denom > 0 ? finiteAbs(bar.delta) / denom : 0,
        bucket: cvdBucket(bar.delta, denom),
        previousFloorAvailable: previousFloor !== null,
      });
    }
    const completeRth = [...new Set(group.map((bar) => bar.rthSession).filter((key): key is string => key !== null))]
      .find((key) => completeSessions.has(key));
    if (completeRth) {
      previousFloor = percentile(group
        .filter((bar) => bar.rthSession === completeRth)
        .map((bar) => finiteAbs(bar.delta)), .9);
    }
    cursor = end;
  }
  return output;
}

export function detectCvdMarkers(
  bars: readonly CvdIntensityBar[],
  candles: readonly CvdPriceRange[],
  previousFullSessionRangeP25: number | null = null,
  completeSessions: ReadonlySet<string> = new Set(),
): CvdHypothesisMarker[] {
  const candleByTime = new Map(candles.map((bar) => [bar.t, bar]));
  const rangeFloorByTime = new Map<number, number>();
  let activeSession: string | null = null;
  let activeRanges: number[] = [];
  let activeRthRanges = new Map<string, number[]>();
  let priorRangeFloor = previousFullSessionRangeP25;
  for (const candle of candles) {
    if (candle.session !== activeSession) {
      const completeRth = [...activeRthRanges.keys()].find((key) => completeSessions.has(key));
      if (completeRth) {
        priorRangeFloor = percentile(activeRthRanges.get(completeRth) ?? [], .25);
      }
      activeSession = candle.session;
      activeRanges = [];
      activeRthRanges = new Map();
    }
    const range = candle.h - candle.l;
    if (!Number.isFinite(range) || range < 0) continue;
    activeRanges.push(range);
    if (candle.rthSession !== null && candle.rthSession !== undefined) {
      const rthRanges = activeRthRanges.get(candle.rthSession) ?? [];
      rthRanges.push(range);
      activeRthRanges.set(candle.rthSession, rthRanges);
    }
    rangeFloorByTime.set(candle.t, Math.max(percentile(activeRanges, .25) ?? 0, priorRangeFloor ?? 0));
  }

  const markers: CvdHypothesisMarker[] = [];
  for (let index = 0; index < bars.length; index += 1) {
    const bar = bars[index];
    const candle = candleByTime.get(bar.t);
    if (bar.bucket !== 3 || !candle) continue;
    if (candle.h - candle.l <= (rangeFloorByTime.get(bar.t) ?? -1)) {
      markers.push({ kind: "ABS", t: bar.t, sourceT: bar.t, label: "ABS?" });
    }

    const tail = bars.slice(index + 1, index + 4);
    if (tail.length !== 3 || tail.some((next) => next.session !== bar.session || next.bucket !== 0)) continue;
    const tailCandles = tail.map((next) => candleByTime.get(next.t));
    if (tailCandles.some((next) => !next)) continue;
    const priceHeld = bar.delta >= 0
      ? tailCandles.every((next) => next!.h <= candle.h)
      : tailCandles.every((next) => next!.l >= candle.l);
    if (priceHeld) {
      markers.push({ kind: "EXH", t: tail[2].t, sourceT: bar.t, label: "EXH?" });
    }
  }
  return markers;
}

/**
 * Amendment E4: calibrated EXH-low, measured only from closed bars. A fresh session low must
 * extend at least three ticks beyond the prior eight-bar low, while the final eight-bar push
 * costs less than 0.4x the preceding leg's average per-tick contracts. Highs stay deliberately
 * absent until the desk supplies a calibrated high-side rule.
 */
export function detectExhaustionLows(bars: readonly ExhaustionLowInput[], W = 8, ratio = .4): ExhaustionLowMark[] {
  if (W <= 0 || !Number.isFinite(W) || !Number.isFinite(ratio) || ratio <= 0) return [];
  const marks: ExhaustionLowMark[] = [];
  let start = 0;
  while (start < bars.length) {
    const session = bars[start].session;
    let end = start + 1;
    while (end < bars.length && bars[end].session === session) end += 1;
    let lastMark = -Infinity;
    const openingLows = bars.slice(start, Math.min(end, start + 20))
      .map((item) => item.priceLow)
      .filter(Number.isFinite);
    let runningLow = openingLows.length ? Math.min(...openingLows) : Number.POSITIVE_INFINITY;
    for (let i = start + 20; i < end; i += 1) {
      const bar = bars[i];
      if (!Number.isFinite(bar.priceLow) || !Number.isFinite(bar.delta)) continue;
      runningLow = Math.min(runningLow, bar.priceLow);
      if (i - lastMark < 10 || bar.priceLow > runningLow) continue;
      const prior = bars.slice(i - W, i).map((item) => item.priceLow).filter(Number.isFinite);
      if (prior.length < W) continue;
      const extension = (Math.min(...prior) - bar.priceLow) / .25;
      if (extension < 3) continue;
      const pushContracts = Math.max(0, -bars.slice(i - W + 1, i + 1)
        .reduce((sum, item) => sum + item.delta, 0));
      const ticks = Math.max(1, extension);
      const pushContractsPerTick = pushContracts / ticks;
      const leg = bars.slice(i - 20, i - W + 1)
        .map((item) => item.delta < 0 ? -item.delta : 0)
        .filter((value) => value > 0);
      const legContractsPerTick = leg.length ? leg.reduce((sum, value) => sum + value, 0) / leg.length : 0;
      if (legContractsPerTick <= 50 || pushContractsPerTick >= ratio * legContractsPerTick) continue;
      marks.push({ t: bar.t, pushContracts, pushContractsPerTick, legContractsPerTick, ticks });
      lastMark = i;
    }
    start = end;
  }
  return marks;
}

export function divergenceWindow(tf: string): number | null {
  if (tf === "12") return 2;
  if (tf === "6") return 3;
  if (tf === "1") return 10;
  return null;
}

export function divergenceContractFloor(tf: string): number {
  return tf === "1" ? 1_500 : 3_000;
}

function isSwing(values: readonly number[], index: number, w: number, side: "high" | "low"): boolean {
  const value = values[index];
  if (!Number.isFinite(value) || index < w || index + w >= values.length) return false;
  for (let at = index - w; at <= index + w; at += 1) {
    if (at === index) continue;
    if (side === "high" ? values[at] > value : values[at] < value) return false;
  }
  return true;
}

export function detectCvdDivergences(
  bars: readonly CvdDivergenceInput[],
  tf: string,
): CvdDivergencePair[] {
  const w = divergenceWindow(tf);
  if (w === null) return [];
  const floor = divergenceContractFloor(tf);
  const cvd = bars.map((bar) => bar.cvd);
  const previous: Partial<Record<"high" | "low", number>> = {};
  const pairs: CvdDivergencePair[] = [];
  for (let index = w; index + w < bars.length; index += 1) {
    for (const side of ["high", "low"] as const) {
      if (!isSwing(cvd, index, w, side)) continue;
      const earlier = previous[side];
      previous[side] = index;
      if (earlier === undefined) continue;
      const contracts = finiteAbs(bars[index].cvd - bars[earlier].cvd);
      if (contracts < floor) continue;
      const cvdExceeds = side === "high" ? bars[index].cvd > bars[earlier].cvd : bars[index].cvd < bars[earlier].cvd;
      const localPrice = (at: number) => {
        const local = bars.slice(Math.max(0, at - 1), Math.min(bars.length, at + 2));
        return side === "high"
          ? Math.max(...local.map((bar) => bar.priceHigh))
          : Math.min(...local.map((bar) => bar.priceLow));
      };
      const previousPrice = side === "high" ? bars[earlier].priceHigh : bars[earlier].priceLow;
      const currentPrice = localPrice(index);
      const priceDoesNot = side === "high" ? currentPrice <= previousPrice : currentPrice >= previousPrice;
      if (!cvdExceeds || !priceDoesNot) continue;
      pairs.push({
        id: pairs.length,
        kind: side === "high" ? "higher-cvd-high" : "lower-cvd-low",
        previousSwingAt: bars[earlier].t,
        swingAt: bars[index].t,
        confirmedAt: bars[index + w].t,
        w,
        contracts,
        previousCvd: bars[earlier].cvd,
        currentCvd: bars[index].cvd,
        previousPrice,
        currentPrice,
        priceTicks: Math.round((currentPrice - previousPrice) / .25),
      });
    }
  }
  return pairs;
}

/** E3 calibrated ABS: price pivots lead; CVD confirms flow without a price result. */
export function detectCalibratedAbsorptions(
  bars: readonly CvdDivergenceInput[], K = 5, minContracts = 600, minPriceTicks = 2,
): CvdDivergencePair[] {
  if (K <= 0 || !Number.isFinite(K) || minContracts <= 0 || minPriceTicks <= 0) return [];
  const pairs: CvdDivergencePair[] = [];
  for (const side of ["high", "low"] as const) {
    const values = bars.map((bar) => side === "high" ? bar.priceHigh : bar.priceLow);
    const pivots = values.flatMap((_, index) => isSwing(values, index, K, side) ? [index] : []);
    for (let at = 1; at < pivots.length; at += 1) {
      const previous = pivots[at - 1]; const current = pivots[at]; const span = current - previous;
      if (span < 6 || span > 40) continue;
      const price = values[current] - values[previous]; const cvd = bars[current].cvd - bars[previous].cvd;
      const absorbed = side === "high"
        ? price <= -minPriceTicks * .25 && cvd >= minContracts
        : price >= minPriceTicks * .25 && cvd <= -minContracts;
      if (!absorbed) continue;
      pairs.push({ id: pairs.length, kind: side === "high" ? "higher-cvd-high" : "lower-cvd-low",
        previousSwingAt: bars[previous].t, swingAt: bars[current].t, confirmedAt: bars[current + K].t,
        w: K, contracts: Math.abs(cvd), previousCvd: bars[previous].cvd, currentCvd: bars[current].cvd,
        previousPrice: values[previous], currentPrice: values[current], priceTicks: Math.round(price / .25) });
    }
  }
  return pairs;
}

export function formatContracts(value: number): string {
  const magnitude = finiteAbs(value);
  return magnitude >= 1_000 ? `${(magnitude / 1_000).toFixed(1)}k` : String(Math.round(magnitude));
}
